import { generateGradient } from '@/utils/file-kit';
import { type ILayerProps, type IVectorLayerProps } from '@/version-types';
import { normalizeStrokeDasharray } from './parse';

/**
 * 提取图层中的渐变
 *
 * @param layer - 图层数据
 */
export const getSVGGradientsFromLayer = (layer: ILayerProps) => {
    const gradientID = Math.random() + new Date().getTime();

    const { width, height } = layer;
    const layerData = layer.data as IVectorLayerProps;
    const { fillGradient, strokeGradient } = layerData;

    const content: string[] = [];

    if (fillGradient) {
        content.push(generateGradient(`FILL_${gradientID}`, width, height, fillGradient));
    }

    if (strokeGradient) {
        content.push(generateGradient(`STROKE_${gradientID}`, width, height, strokeGradient));
    }

    if (content.length === 0) return null;

    return {
        id: String(gradientID),
        content: content.join(''),
    };
};

/**
 * 从图层数据中提取样式
 *
 * @param data - 图层数据
 * @param options - 可选；配置参数；
 *
 *  - `options.ratio` 缩放比例 (需要内描边裁剪时为 2， 无内描边时为 1)；
 *  - `options.gradientID` - 渐变 ID
 *
 * @returns 样式
 */
export const getStylesFromLayer = (layer: ILayerProps, options: { ratio: number; gradientID?: string }) => {
    const layerData = layer.data as IVectorLayerProps;
    const { fill, fillGradient, stroke, strokeGradient, strokeWidth, strokeDasharray, strokeLinecap, strokeHidden } =
        layerData;
    const { ratio = 2, gradientID } = options;

    const fillStyle = `fill:${fillGradient && gradientID ? `url(#FILL_${gradientID})` : fill};`;

    const strokeStyles: string[] = [];
    if ((stroke || strokeGradient) && !strokeHidden) {
        strokeStyles.push(`stroke:${strokeGradient && gradientID ? `url(#STROKE_${gradientID})` : stroke};`);
        strokeStyles.push(`stroke-width:${strokeWidth * ratio};`);
        if (strokeDasharray) {
            strokeStyles.push(`stroke-dasharray:${normalizeStrokeDasharray(strokeDasharray, strokeWidth)};`);
        }
        strokeStyles.push(`stroke-linecap:${strokeLinecap || 'butt'};`);
    }

    return {
        fillStyle,
        strokeStyle: strokeStyles.join(''),
    };
};

/**
 * 将样式对象转换为样式字符串
 *
 * @param styleObject - 样式对象
 * @returns 样式字符串
 */
export const toStringStyle = (styleObject: Record<string, any>): string => {
    if (!styleObject || typeof styleObject !== 'object') {
        return '';
    }

    return Object.entries(styleObject)
        .map(([key, value]) => {
            const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            const cssValue =
                typeof value === 'number' && value !== 0 && !['opacity', 'z-index', 'font-weight'].includes(cssKey)
                    ? `${value}px`
                    : value;
            return `${cssKey}:${cssValue}`;
        })
        .join(';');
};
